/**
 * Structured logger for the frontend application.
 *
 * Uses Grafana Faro to ship logs to Grafana Cloud Frontend Observability.
 * Faro's collector endpoint supports browser CORS (unlike the OTLP gateway).
 *
 * The public API (logger.info / warn / error / debug / flush) is unchanged
 * so all existing call-sites continue to work without modification.
 *
 * Environment variables (set via .env / .env.production):
 *   REACT_APP_FARO_URL      – Grafana Faro collector URL
 *   REACT_APP_FARO_APP_NAME – application name (default: xquant-frontend)
 */

import { faro, LogLevel as FaroLogLevel } from '@grafana/faro-react';

// ---- types (kept for backward compatibility) --------------------------------

export enum LogLevel {
  DEBUG = 1,
  INFO = 5,
  WARN = 9,
  ERROR = 13,
}

// Map our LogLevel to Faro's LogLevel
function toFaroLevel(level: LogLevel): FaroLogLevel {
  switch (level) {
    case LogLevel.DEBUG: return FaroLogLevel.DEBUG;
    case LogLevel.INFO:  return FaroLogLevel.INFO;
    case LogLevel.WARN:  return FaroLogLevel.WARN;
    case LogLevel.ERROR: return FaroLogLevel.ERROR;
    default:             return FaroLogLevel.INFO;
  }
}

// ---- core log function -----------------------------------------------------

function log(level: LogLevel, message: string, ...rest: unknown[]): void {
  // If Faro is not initialized, silently discard
  if (!faro.api) return;

  const context: Record<string, string> = {};

  // Normalise the variadic args into structured context.
  // Accepts:
  //   log(LVL, 'msg')
  //   log(LVL, 'msg', { key: val })
  //   log(LVL, 'msg', val1, val2, ...)   ← legacy console-style
  if (rest.length === 1 && rest[0] && typeof rest[0] === 'object' && !Array.isArray(rest[0]) && !(rest[0] instanceof Error)) {
    for (const [k, v] of Object.entries(rest[0] as Record<string, unknown>)) {
      context[k] = String(v);
    }
  } else if (rest.length > 0) {
    const detail = rest.map(a => {
      if (a instanceof Error) return `${a.name}: ${a.message}`;
      if (typeof a === 'object') { try { return JSON.stringify(a); } catch { return String(a); } }
      return String(a);
    }).join(' ');
    context['detail'] = detail;
  }

  try {
    faro.api.pushLog([message], {
      level: toFaroLevel(level),
      context,
    });
  } catch {
    // Graceful degradation — never let logging break the app
  }
}

// ---- public API (unchanged) ------------------------------------------------

const logger = {
  debug: (msg: string, ...rest: unknown[]) => log(LogLevel.DEBUG, msg, ...rest),
  info:  (msg: string, ...rest: unknown[]) => log(LogLevel.INFO,  msg, ...rest),
  warn:  (msg: string, ...rest: unknown[]) => log(LogLevel.WARN,  msg, ...rest),
  error: (msg: string, ...rest: unknown[]) => log(LogLevel.ERROR, msg, ...rest),
  /** No-op — Faro handles batching/flushing internally. Kept for API compat. */
  flush: () => {},
};

export default logger;
